import { useCallback, useRef, useState } from "react";
import { message } from "antd";
import { UseAgreeGateModal, type AgreePromptOptions } from "./UseAgreeGateModal";

export type { AgreePromptOptions };

type UseAgreeGateOptions = {
  agreed: boolean;
  setAgreed: (agreed: boolean) => void;
  uncheckedTip?: string;
};

type UseAgreeGateResult = {
  runWithAgree: (action: () => void, prompt?: AgreePromptOptions) => void;
  agreePromptModal: React.ReactNode;
};

export function useAgreeGate(options: UseAgreeGateOptions): UseAgreeGateResult {
  const { agreed, setAgreed, uncheckedTip = "请先勾选同意" } = options;
  const [open, setOpen] = useState(false);
  const [promptChecked, setPromptChecked] = useState(false);
  const [promptOptions, setPromptOptions] = useState<AgreePromptOptions | null>(null);
  const pendingRef = useRef<(() => void) | null>(null);

  const runWithAgree = useCallback(
    (action: () => void, prompt?: AgreePromptOptions) => {
      if (agreed) {
        action();
        return;
      }
      pendingRef.current = action;
      setPromptChecked(false);
      setPromptOptions(prompt ?? null);
      setOpen(true);
    },
    [agreed],
  );

  const closePrompt = useCallback(() => {
    setOpen(false);
    pendingRef.current = null;
    setPromptOptions(null);
  }, []);

  const confirmPrompt = useCallback(() => {
    if (!promptChecked) {
      message.warning(uncheckedTip);
      return;
    }
    setAgreed(true);
    setOpen(false);
    const action = pendingRef.current;
    pendingRef.current = null;
    setPromptOptions(null);
    action?.();
  }, [promptChecked, setAgreed, uncheckedTip]);

  const agreePromptModal = promptOptions ? (
    <UseAgreeGateModal
      open={open}
      promptOptions={promptOptions}
      promptChecked={promptChecked}
      onCheckedChange={setPromptChecked}
      onCancel={closePrompt}
      onConfirm={confirmPrompt}
    />
  ) : null;

  return { runWithAgree, agreePromptModal };
}
